/**
 * Export Manager
 * Main orchestrator for all export operations
 */

import { Model } from '../../types';
import {
    ExportFormat,
    ExportConfig,
    ExportResult,
    ExportHandler,
    ExportProgressCallback,
} from './exportTypes';
import {
    createDefaultConfig,
    validateExportConfig,
    downloadBlob,
    downloadString,
    getMimeType,
} from './exportUtils';
import { createPNGExportHandler } from './exportFormats/pngExport';
import { createJPEGExportHandler } from './exportFormats/jpegExport';
import { createSVGExportHandler } from './exportFormats/svgExport';
import { createJSONExportHandler } from './exportFormats/jsonExport';
import { createMarkdownExportHandler } from './exportFormats/markdownExport';
import { createGIFExportHandler } from './exportFormats/gifExport';

/**
 * Main Export Manager
 * Coordinates all export operations
 */
export class ExportManager {
    private handlers: Map<ExportFormat, ExportHandler>;
    private defaultConfig: ExportConfig;

    constructor() {
        // Initialize export handlers
        this.handlers = new Map();
        this.registerHandlers();

        // Set default configuration
        this.defaultConfig = createDefaultConfig('png');
    }

    /**
     * Register all export handlers
     */
    private registerHandlers(): void {
        this.handlers.set('png', createPNGExportHandler());
        this.handlers.set('jpeg', createJPEGExportHandler());
        this.handlers.set('svg', createSVGExportHandler());
        this.handlers.set('gif', createGIFExportHandler());
        this.handlers.set('json', createJSONExportHandler());
        this.handlers.set('markdown', createMarkdownExportHandler());
    }

    /**
     * Export diagram
     */
    async export(
        element: HTMLElement,
        model: Model,
        config: Partial<ExportConfig> = {},
        onProgress?: ExportProgressCallback
    ): Promise<ExportResult> {
        try {
            // Merge with defaults
            const fullConfig: ExportConfig = {
                ...this.defaultConfig,
                ...config,
            };

            // Validate configuration
            if (!validateExportConfig(fullConfig)) {
                throw new Error('Invalid export configuration');
            }

            onProgress?.(0, 'Starting export...');

            // Get handler for format
            const handler = this.handlers.get(fullConfig.format);
            if (!handler) {
                throw new Error(`Unsupported export format: ${fullConfig.format}`);
            }

            // Perform export
            const result = await handler.export(element, model, fullConfig, onProgress);

            return result;
        } catch (error) {
            return {
                success: false,
                filename: '',
                error: error as Error,
            };
        }
    }

    /**
     * Export and immediately download
     */
    async exportAndDownload(
        element: HTMLElement,
        model: Model,
        config: Partial<ExportConfig> = {},
        onProgress?: ExportProgressCallback
    ): Promise<ExportResult> {
        const result = await this.export(element, model, config, onProgress);

        if (result.success && result.data) {
            await this.download(result);
        }

        return result;
    }

    /**
     * Download export result
     */
    async download(result: ExportResult): Promise<void> {
        if (!result.success || !result.data) {
            throw new Error('Cannot download failed export');
        }

        if (result.data instanceof Blob) {
            downloadBlob(result.data, result.filename);
        } else if (typeof result.data === 'string') {
            const mimeType = getMimeType(result.format!);
            downloadString(result.data, result.filename, mimeType);
        } else {
            throw new Error('Unsupported data type for download');
        }
    }

    /**
     * Get supported formats
     */
    getSupportedFormats(): ExportFormat[] {
        return Array.from(this.handlers.keys());
    }

    /**
     * Check if format is supported
     */
    isFormatSupported(format: ExportFormat): boolean {
        return this.handlers.has(format);
    }

    /**
     * Get default configuration
     */
    getDefaultConfig(): ExportConfig {
        return { ...this.defaultConfig };
    }

    /**
     * Update default configuration
     */
    updateDefaultConfig(config: Partial<ExportConfig>): void {
        this.defaultConfig = {
            ...this.defaultConfig,
            ...config,
        };
    }

    /**
     * Validate configuration
     */
    validateConfig(config: Partial<ExportConfig>): boolean {
        return validateExportConfig(config);
    }

    /**
     * Export to PNG (convenience method)
     */
    async exportToPNG(
        element: HTMLElement,
        model: Model,
        quality: 1 | 2 | 4 = 2,
        transparent: boolean = false
    ): Promise<ExportResult> {
        return this.exportAndDownload(element, model, {
            format: 'png',
            quality,
            transparent,
        });
    }

    /**
     * Export to JPEG (convenience method)
     */
    async exportToJPEG(
        element: HTMLElement,
        model: Model,
        quality: 1 | 2 | 4 = 2,
        backgroundColor: string = '#ffffff'
    ): Promise<ExportResult> {
        return this.exportAndDownload(element, model, {
            format: 'jpeg',
            quality,
            backgroundColor,
        });
    }

    /**
     * Export to SVG (convenience method)
     */
    async exportToSVG(
        element: HTMLElement,
        model: Model,
        transparent: boolean = false
    ): Promise<ExportResult> {
        return this.exportAndDownload(element, model, {
            format: 'svg',
            transparent,
        });
    }

    /** Export an animated GIF. */
    async exportToGIF(
        element: HTMLElement,
        model: Model,
        durationSeconds: number = 3,
        fps: number = 12
    ): Promise<ExportResult> {
        return this.exportAndDownload(element, model, {
            format: 'gif',
            quality: 1,
            gifDurationSeconds: durationSeconds,
            gifFps: fps,
            gifLoop: true,
        });
    }

    /**
     * Export to JSON (convenience method)
     */
    async exportToJSON(element: HTMLElement, model: Model): Promise<ExportResult> {
        return this.exportAndDownload(element, model, {
            format: 'json',
        });
    }

    /**
     * Batch export to multiple formats
     */
    async exportBatch(
        element: HTMLElement,
        model: Model,
        formats: ExportFormat[],
        config: Partial<ExportConfig> = {},
        onProgress?: (format: ExportFormat, progress: number, message: string) => void
    ): Promise<ExportResult[]> {
        const results: ExportResult[] = [];

        for (const format of formats) {
            const formatConfig = {
                ...config,
                format,
            };

            const progressCallback: ExportProgressCallback = (progress, message) => {
                onProgress?.(format, progress, message);
            };

            const result = await this.exportAndDownload(
                element,
                model,
                formatConfig,
                progressCallback
            );
            results.push(result);
        }

        return results;
    }

    /**
     * Get export preview (without downloading)
     */
    async getPreview(
        element: HTMLElement,
        model: Model,
        format: ExportFormat = 'png'
    ): Promise<string | null> {
        const result = await this.export(element, model, { format });

        if (!result.success || !result.data) {
            return null;
        }

        if (result.data instanceof Blob) {
            return URL.createObjectURL(result.data);
        } else if (typeof result.data === 'string') {
            if (format === 'svg') {
                return `data:image/svg+xml;base64,${btoa(result.data)}`;
            } else if (format === 'json') {
                return `data:application/json;base64,${btoa(result.data)}`;
            }
        }

        return null;
    }

    /**
     * Clean up resources
     */
    dispose(): void {
        this.handlers.clear();
    }
}

/**
 * Create a singleton export manager instance
 */
export const createExportManager = (): ExportManager => {
    return new ExportManager();
};

/**
 * Export the types for external use
 */
export type { ExportFormat, ExportConfig, ExportResult, ExportProgressCallback };
